import { getSession } from '../auth';
import { toast } from '../components/toast';
import { isProductionData, listBatches, listCourses, listStudentsForBatch, writeAttendance } from '../lib/repository';
import { cycleMark, ratioFor, statusClass, labelFor } from '../lib/attendance';
import type { BatchRecord, CourseRecord, StudentRecord } from '../domain';

type Mode = 'pick-course' | 'pick-batch' | 'grid';
type StuMark = 'P' | 'A' | 'L' | 'E';

function readState(): { mode: Mode; courseId: string; batchId: string } {
  const h = location.hash;
  const qs = h.includes('?') ? h.split('?')[1] : '';
  const params = new URLSearchParams(qs);
  const mode = (params.get('s') as Mode) || 'pick-course';
  return { mode: mode === 'pick-batch' || mode === 'grid' ? mode : 'pick-course', courseId: params.get('c') || '', batchId: params.get('b') || '' };
}
function writeState(mode: Mode, courseId: string, batchId: string): void {
  const qs = new URLSearchParams();
  if (mode !== 'pick-course') qs.set('s', mode);
  if (courseId) qs.set('c', courseId);
  if (batchId) qs.set('b', batchId);
  const tail = qs.toString();
  location.hash = '#/attendance' + (tail ? '?' + tail : '');
}

let courses: CourseRecord[] = [];
let coursesLoaded = false;
let coursesLoading = false;
let batches: BatchRecord[] = [];
let batchesLoaded = false;
let batchesLoading = false;
let roster: StudentRecord[] = [];
let rosterLoading = false;

async function loadCourses(): Promise<void> {
  if (coursesLoaded || coursesLoading || !isProductionData()) return;
  coursesLoading = true; try { courses = await listCourses(); } catch { courses = []; }
  coursesLoaded = true; coursesLoading = false; (window as any).__rerender?.();
}
async function loadBatches(): Promise<void> {
  if (batchesLoaded || batchesLoading || !isProductionData()) return;
  batchesLoading = true; try { batches = await listBatches(); } catch { batches = []; }
  batchesLoaded = true; batchesLoading = false; (window as any).__rerender?.();
}
async function loadRoster(batchId: string): Promise<void> {
  if (!batchId) { roster = []; return; }
  rosterLoading = true; try { roster = await listStudentsForBatch(batchId); } catch { roster = []; }
  rosterLoading = false; (window as any).__rerender?.();
}

function lastDays(n: number): string[] {
  const out: string[] = [];
  for (let i = n - 1; i >= 0; i--) { const d = new Date(); d.setDate(d.getDate() - i); out.push(d.toISOString().slice(0, 10)); }
  return out;
}

// Local-first cache: every click hits here FIRST (sync) so the chip updates
// immediately, even if Firestore hasn't responded. Cache survives reload so
// offline-UX is preserved. Firestore is the source of truth on next load.
const LOCAL_KEY_PREFIX = 'clt-attendance-cache-';
function localCacheFor(batchId: string): Record<string, StuMark> {
  try { return JSON.parse(localStorage.getItem(LOCAL_KEY_PREFIX + batchId) || '{}'); } catch { return {}; }
}
function localCacheSet(batchId: string, uid: string, date: string, status: StuMark): void {
  try { const m = localCacheFor(batchId); m[uid + ':' + date] = status; localStorage.setItem(LOCAL_KEY_PREFIX + batchId, JSON.stringify(m)); } catch { /* quota */ }
}

export function renderAttendance(): string {
  void loadCourses(); void loadBatches();
  const { mode, courseId, batchId } = readState();
  if (mode === 'grid' && batchId) void loadRoster(batchId);
  const dates = lastDays(7);

  if (mode === 'pick-course' || !courseId) {
    const courseCards = courses.map((c) => {
      const batchCount = batches.filter((b) => b.courseId === c.id).length;
      return '<button type="button" class="card card-pad-lg att-pick" data-pick-course="' + c.id + '" style="text-align:left;cursor:pointer;width:100%;border:1px solid var(--rule);border-radius:14px;padding:18px;background:var(--bg-elev)">' +
        '<div class="row"><span class="pill ' + (c.status === 'live' ? 'pill-success' : 'pill-info') + '">' + (c.status || 'draft') + '</span><span class="spacer"></span><span class="t-mono t-meta">' + (c.code || '—') + '</span></div>' +
        '<h3 class="t-h3" style="margin:10px 0 6px">' + (c.title || 'Untitled course') + '</h3>' +
        '<div class="t-meta">' + (c.durationWeeks || 6) + ' weeks · ' + (c.level || 'Foundation') + ' · ' + batchCount + ' ' + (batchCount === 1 ? 'batch' : 'batches') + '</div></div></div></button>';
    }).join('');
    const emptyCopy = coursesLoading ? '<div class="card card-pad-lg empty"><div class="empty-illus">⏳</div><h2>Loading courses…</h2></div>'
      : '<div class="card card-pad-lg empty"><div class="empty-illus">🎓</div><h2>No courses yet</h2><p style="font-size:var(--t-sm)">Create a course first, then come back to mark attendance.</p></div>';
    return '<div class="screen-head"><div><div class="t-eyebrow">Step 1 of 3</div><h1 class="t-h1">Pick a course</h1><p class="t-meta">Attendance is grouped per course.</p></div></div><div class="att-picker-grid">' + (courseCards || emptyCopy) + '</div>';
  }

  if (mode === 'pick-batch' || !batchId) {
    const course = courses.find((c) => c.id === courseId);
    const courseBatches = batches.filter((b) => b.courseId === courseId);
    const batchCards = courseBatches.map((b) => '<button type="button" class="card card-pad-lg att-pick" data-pick-batch="' + b.id + '" style="text-align:left;cursor:pointer;width:100%;border:1px solid var(--rule);border-radius:14px;padding:18px;background:var(--bg-elev)">' +
      '<div class="row"><span class="pill ' + (b.status === 'live' ? 'pill-success' : 'pill-info') + '">' + (b.status || 'draft') + '</span><span class="spacer"></span><span class="t-mono t-meta">' + (b.startDate ? b.startDate.slice(5) : '—') + '</span></div>' +
      '<h3 class="t-h3" style="margin:10px 0 6px">' + (b.name || 'Untitled batch') + '</h3>' +
      '<div class="t-meta">' + (b.schedule || '—') + ' · capacity ' + (b.capacity || 0) + '</div></div></div></button>').join('');
    const emptyCopy = batchesLoading ? '<div class="card card-pad-lg empty"><div class="empty-illus">⏳</div><h2>Loading batches…</h2></div>'
      : '<div class="card card-pad-lg empty"><div class="empty-illus">📚</div><h2>No batches for this course yet</h2><p style="font-size:var(--t-sm)">Create a batch in the Batches tab first.</p></div>';
    return '<div class="screen-head"><div><button type="button" class="btn btn-ghost touch-target att-back" data-back-to-courses>← Courses</button>' +
      '<div><div class="t-eyebrow">Step 2 of 3</div><h1 class="t-h1">' + (course?.title || 'Pick a batch') + '</h1>' +
      '<p class="t-meta">Select the batch you want to mark attendance for.</p></div></div></div><div class="att-picker-grid">' + (batchCards || emptyCopy) + '</div>';
  }

  const batch = batches.find((b) => b.id === batchId);
  const course = courses.find((c) => c.id === batch?.courseId);
  const localCache = localCacheFor(batchId);
  const subtitle = rosterLoading ? 'Loading roster…' : !roster.length ? (batch?.name || 'No batch') + ' · add learners to start' : roster.length + ' ' + (roster.length === 1 ? 'learner' : 'learners') + ' · last 7 days';

  let body: string;
  if (!batch) {
    body = '<div class="attendance-empty"><h3>Batch not found</h3><p>It may have been deleted. <a href="#/attendance" data-back-to-courses style="color:var(--accent)">Back to courses</a></p></div>';
  } else if (rosterLoading && !roster.length) {
    body = '<div class="attendance-empty"><h3>Loading roster…</h3></div>';
  } else if (!roster.length) {
    body = '<div class="attendance-empty"><h3>No learners in this batch yet</h3><p>Add learners from the Batches tab.</p></div>';
  } else {
    const headRow = '<div class="ag-row ag-head-row"><div class="ag-cell ag-head ag-name-head">Learner</div>' +
      dates.map((d) => '<div class="ag-cell ag-head" title="' + d + '">' + d.slice(5) + '</div>').join('') +
      '<div class="ag-cell ag-head ag-pct-head">%</div></div>';
    const rows = roster.map((s) => {
      const cells = dates.map((d) => {
        const cached = localCache[s.id + ':' + d];
        const startChar = cached || '–';
        return '<div class="ag-cell" data-toggle="students" data-person-code="' + s.id + '" data-date="' + d + '" title="Click to cycle P → A → L → E" style="cursor:pointer">' +
          '<span class="ag-attend ' + (cached ? statusClass(cached) : '') + '">' + (cached === 'P' ? '✓' : startChar) + '</span></div>';
      }).join('');
      return '<div class="ag-row" data-row-id="' + s.id + '"><div class="ag-cell ag-name">' + (s.name || '?') + '</div>' + cells + '<div class="ag-cell"><span class="ag-pct">–</span></div></div>';
    }).join('');
    body = '<div class="card" style="padding:8px 12px;margin-bottom:10px;font-size:12px"><span class="t-meta">Click a cell to cycle:</span> ' +
      '<span class="pill st-P" style="font-size:10px">P</span> Present · <span class="pill st-A" style="font-size:10px">A</span> Absent · ' +
      '<span class="pill st-L" style="font-size:10px">L</span> Late · <span class="pill st-E" style="font-size:10px">E</span> Excused</div>' +
      '<div class="attendance-grid-wrap card"><div class="attendance-grid">' + headRow + rows + '</div></div>';
  }
  const saveDisabled = !batch || !roster.length;
  return '<div class="screen-head"><div><button type="button" class="btn btn-ghost touch-target att-back" data-back-to-batches>← Batches</button>' +
    '<div><div class="t-eyebrow">Step 3 of 3 · ' + (course?.title || '—') + '</div>' +
    '<h1 class="t-h1">' + (batch?.name || 'Attendance grid') + '</h1>' +
    '<p class="t-meta">' + subtitle + '</p></div>' +
    (batch ? '<button type="button" id="att-save" class="btn btn-primary touch-target" ' + (saveDisabled ? 'disabled style="opacity:0.5;cursor:not-allowed"' : '') + '>Save marks</button>' : '') + '</div></div>' + body;
}

export function bindAttendance(): void {
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const session = getSession();
    if (session?.role === 'learner') return;

    const pickCourse = t.closest<HTMLElement>('[data-pick-course]');
    if (pickCourse && pickCourse.dataset.pickCourse) { writeState('pick-batch', pickCourse.dataset.pickCourse, ''); (window as any).__rerender?.(); return; }
    const pickBatch = t.closest<HTMLElement>('[data-pick-batch]');
    if (pickBatch && pickBatch.dataset.pickBatch) { const st = readState(); writeState('grid', st.courseId, pickBatch.dataset.pickBatch); (window as any).__rerender?.(); void loadRoster(pickBatch.dataset.pickBatch); return; }
    const backCourses = t.closest<HTMLElement>('[data-back-to-courses]');
    if (backCourses) { e.preventDefault(); writeState('pick-course', '', ''); (window as any).__rerender?.(); return; }
    const backBatches = t.closest<HTMLElement>('[data-back-to-batches]');
    if (backBatches) { e.preventDefault(); const st = readState(); writeState('pick-batch', st.courseId, ''); (window as any).__rerender?.(); return; }

    const cell = t.closest<HTMLElement>('[data-toggle="students"]');
    if (!cell) return;
    const st = readState();
    if (!st.batchId) { toast('Pick a batch first', 'warn'); return; }
    const code = cell.dataset.personCode || '';
    const date = cell.dataset.date || '';
    if (!code || !date) { toast('Cell data missing — refresh and try again', 'warn'); return; }

    // 1) Cycle locally via STUDENT order (P/A/L/E only) — never reaches staff statuses.
    const raw = cycleMark('students', code, date) as StuMark;
    // 2) Mirror immediately to local cache — survives reload, no Firestore round-trip.
    localCacheSet(st.batchId, code, date, raw);
    // 3) Update the chip synchronously so the user ALWAYS sees feedback.
    const chip = cell.querySelector<HTMLElement>('.ag-attend');
    if (chip) { chip.className = 'ag-attend ' + statusClass(raw); chip.textContent = raw === 'P' ? '✓' : raw; }
    // 4) Recompute row ratio.
    const row = t.closest<HTMLElement>('[data-row-id]');
    if (row) {
      const ratio = ratioFor('students', code, lastDays(7));
      const pct = row.querySelector<HTMLElement>('.ag-pct');
      if (pct) pct.textContent = ratio + '%';
    }
    // 5) Fire-and-forget the Firestore write so the data persists. toast tells
    //    the user about server-side errors; the chip update above is independent.
    void writeAttendance({ batchId: st.batchId, studentUid: code, date, status: raw })
      .then(() => toast(labelFor(raw) + ' · ' + date.slice(5) + ' · saved', 'success'))
      .catch((err) => toast('Saved locally · server will retry: ' + (err instanceof Error ? err.message : 'write failed'), 'warn'));
  });

  document.addEventListener('click', (e) => {
    const save = (e.target as HTMLElement).closest('#att-save');
    if (save && !save.hasAttribute('disabled')) toast('All marks cached locally · Firestore is being kept in sync.', 'success');
  });
}